import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import DisclaimerModal from "./DisclaimerModal";
import { hasAcknowledgedCurrent } from "../lib/disclaimer";

export default function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div
        data-testid="auth-loading"
        className="min-h-screen flex items-center justify-center text-[var(--pa-text-muted)]"
      >
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;

  // BAA re-consent gate — admin is the platform operator and is exempt.
  // Triggers when (a) the user has never accepted, OR (b) the agreement
  // version has been bumped server-side since their last acceptance.
  const baaCurrent = (user.baa_current_version || "").trim();
  const baaAccepted = (user.baa_accepted_version || "").trim();
  const baaStale = !user.baa_accepted_at || (baaCurrent && baaAccepted !== baaCurrent);
  if (!user.is_admin && baaStale && location.pathname !== "/accept-baa") {
    return <Navigate to="/accept-baa" replace />;
  }

  // Professional self-attestation gate (2026-08). Fires ONCE per user
  // after the BAA gate is cleared. Admins bypass. Presence of the
  // timestamp on the /auth/me response is the only signal used here.
  if (
    !user.is_admin &&
    !user.self_attested_at &&
    location.pathname !== "/self-attestation" &&
    location.pathname !== "/accept-baa"
  ) {
    return <Navigate to="/self-attestation" replace />;
  }

  // 2FA mandatory-enrollment gate — after 7-day grace, all users must enroll
  // before they can use any protected route. Admins are NOT exempt — they hold
  // the highest privileges and need 2FA most.
  if (
    user.needs_2fa_enrollment &&
    !user.totp_enabled &&
    location.pathname !== "/enroll-2fa" &&
    location.pathname !== "/accept-baa" &&
    location.pathname !== "/self-attestation"
  ) {
    return <Navigate to="/enroll-2fa" replace />;
  }

  // Clinical Decision Support disclaimer — show a blocking modal until the
  // current version is acknowledged. We render the modal *on top of* the
  // protected page so deep links still feel natural after acknowledgment.
  const needsDisclaimer =
    !hasAcknowledgedCurrent(user) &&
    location.pathname !== "/accept-baa" &&
    location.pathname !== "/enroll-2fa" &&
    location.pathname !== "/self-attestation";

  return (
    <>
      {children}
      {needsDisclaimer && <DisclaimerModal />}
    </>
  );
}
